/**
 * Human problem language for OZK life topics.
 *
 * Topic names remain the stable knowledge-graph key. These records describe
 * the situation in the words a person is more likely to recognise or search.
 */

export type TopicProblemUrgency = "low" | "medium" | "high";
export type TopicProblemLocale = "ko" | "en";

export type TopicProblem = {
  topicId: string;
  urgency: TopicProblemUrgency;
  problem: Record<TopicProblemLocale, string>;
  situation: Record<TopicProblemLocale, string>;
  firstAction: Record<TopicProblemLocale, string>;
  searchQueries: Record<TopicProblemLocale, readonly string[]>;
};

export const TOPIC_PROBLEMS: readonly TopicProblem[] = [
  {
    topicId: "abn",
    urgency: "medium",
    problem: { ko: "ABN을 신청해야 하는지, 어디서 시작해야 하는지 모르겠어요", en: "I am not sure whether I need an ABN or where to start" },
    situation: { ko: "프리랜서·배달·개인사업을 시작하거나 신청이 거절된 경우", en: "Starting freelance, delivery or sole-trader work, or dealing with a refused application" },
    firstAction: { ko: "직원인지 계약자인지와 ABN 신청 자격부터 확인하세요.", en: "First check whether you are an employee or contractor and whether you are eligible." },
    searchQueries: { ko: ["ABN 신청 거절", "우버하려면 ABN 필요한가요", "ABN 신청 어떻게"], en: ["ABN application refused", "do I need an ABN for Uber", "how to apply for ABN"] },
  },
  {
    topicId: "tfn",
    urgency: "medium",
    problem: { ko: "TFN이 아직 안 왔거나 번호를 잃어버렸어요", en: "My TFN has not arrived or I have lost the number" },
    situation: { ko: "취업을 시작했거나 처음 세금 번호를 신청하는 경우", en: "Starting a job or applying for a tax file number for the first time" },
    firstAction: { ko: "본인 체류 상태에 맞는 ATO 공식 신청 경로를 확인하세요.", en: "Use the official ATO application route that matches your residency status." },
    searchQueries: { ko: ["TFN 안 왔어요", "TFN 잃어버렸어요", "호주 TFN 신청"], en: ["TFN not received", "lost my TFN", "apply for TFN Australia"] },
  },
  {
    topicId: "gst",
    urgency: "high",
    problem: { ko: "매출이 늘었는데 GST 등록이나 BAS 신고를 놓친 것 같아요", en: "My revenue increased and I may have missed GST registration or a BAS deadline" },
    situation: { ko: "개인사업 매출이 기준에 가까워졌거나 신고 기한이 지난 경우", en: "Business turnover is near the threshold or a reporting deadline has passed" },
    firstAction: { ko: "현재 매출과 등록 의무 발생 시점을 먼저 계산하세요.", en: "Check your current turnover and when the registration obligation began." },
    searchQueries: { ko: ["GST 등록 늦었어요", "BAS 신고 기한 놓쳤어요", "GST 매출 기준"], en: ["late GST registration", "missed BAS deadline", "GST turnover threshold"] },
  },
  {
    topicId: "nsw-driver-licence",
    urgency: "high",
    problem: { ko: "한국 면허로 언제까지 운전할 수 있는지 모르겠어요", en: "I do not know how long I can drive in NSW on my Korean licence" },
    situation: { ko: "NSW에 정착했거나 한국 면허 전환 기한이 걱정되는 경우", en: "Settling in NSW or worried about the licence conversion deadline" },
    firstAction: { ko: "현재 비자·거주 상태와 NSW 입국·정착 날짜를 확인하세요.", en: "Check your visa or residency status and the date you began living in NSW." },
    searchQueries: { ko: ["한국 면허 NSW 전환 기한", "한국 면허로 호주 운전 가능 기간", "NSW 면허 서류"], en: ["convert Korean licence NSW deadline", "driving in NSW with Korean licence", "NSW licence documents"] },
  },
  {
    topicId: "medicare",
    urgency: "high",
    problem: { ko: "Medicare 신청이 늦어지거나 병원비를 어떻게 처리할지 모르겠어요", en: "My Medicare application is delayed or I do not know how to handle a medical bill" },
    situation: { ko: "카드가 없거나 신청 중에 GP·병원을 이용해야 하는 경우", en: "Needing a GP or hospital while the card is missing or the application is pending" },
    firstAction: { ko: "현재 Medicare 자격과 신청 진행 상태부터 확인하세요.", en: "Check your Medicare eligibility and application status first." },
    searchQueries: { ko: ["Medicare 카드 안 왔어요", "메디케어 없이 병원", "메디케어 신청 거절"], en: ["Medicare card not received", "doctor without Medicare", "Medicare application rejected"] },
  },
  {
    topicId: "centrelink",
    urgency: "high",
    problem: { ko: "Centrelink 신청이 거절되거나 지급이 갑자기 중단됐어요", en: "My Centrelink claim was rejected or my payment suddenly stopped" },
    situation: { ko: "신청 결과에 이의가 있거나 추가 서류 요청을 받은 경우", en: "Disputing a decision or responding to a request for more documents" },
    firstAction: { ko: "결정문에서 거절·중단 이유와 대응 기한을 확인하세요.", en: "Read the decision letter for the reason and response deadline." },
    searchQueries: { ko: ["Centrelink 신청 거절", "센터링크 지급 중단", "Centrelink 이의신청"], en: ["Centrelink claim rejected", "Centrelink payment stopped", "appeal Centrelink decision"] },
  },
  {
    topicId: "student-visa",
    urgency: "high",
    problem: { ko: "학생비자 기한이나 학교 변경이 비자에 영향을 줄까 걱정돼요", en: "I am worried a deadline or changing schools will affect my student visa" },
    situation: { ko: "비자 만료가 다가오거나 CoE·학교 상황이 달라진 경우", en: "The visa is expiring or the CoE or study situation has changed" },
    firstAction: { ko: "VEVO의 현재 조건과 비자·CoE 만료일을 확인하세요.", en: "Check current VEVO conditions and the visa and CoE expiry dates." },
    searchQueries: { ko: ["학생비자 만료 놓쳤어요", "학교 바꾸면 학생비자", "CoE 취소 비자"], en: ["student visa expired", "changing school student visa", "CoE cancelled visa"] },
  },
  {
    topicId: "working-holiday",
    urgency: "medium",
    problem: { ko: "워홀로 왔는데 집·일·세금부터 무엇을 해야 할지 막막해요", en: "I arrived on a working holiday and do not know what to do first for housing, work and tax" },
    situation: { ko: "출국 준비 중이거나 호주 도착 후 첫 3개월인 경우", en: "Preparing to leave or in the first three months after arriving" },
    firstAction: { ko: "비자 조건, 초기 숙소, 은행계좌와 TFN 순서로 확인하세요.", en: "Start with visa conditions, initial accommodation, a bank account and TFN." },
    searchQueries: { ko: ["호주 워홀 도착 후 할 일", "워홀 일 못 구해요", "세컨비자 농장 조건"], en: ["first things working holiday Australia", "cannot find working holiday job", "second working holiday farm requirements"] },
  },
  {
    topicId: "first-home-buyer",
    urgency: "medium",
    problem: { ko: "첫 집을 사고 싶은데 대출 가능 금액과 지원 자격을 모르겠어요", en: "I want to buy my first home but do not know my borrowing limit or support eligibility" },
    situation: { ko: "예산을 세우거나 pre-approval·계약을 준비하는 경우", en: "Setting a budget or preparing for pre-approval and a contract" },
    firstAction: { ko: "보유 자금, 소득, 부채와 예상 부대비용을 먼저 정리하세요.", en: "List your savings, income, debts and expected purchase costs first." },
    searchQueries: { ko: ["호주 첫 집 지원 자격", "대출 얼마나 가능", "stamp duty first home buyer"], en: ["first home buyer eligibility Australia", "how much can I borrow", "first home buyer stamp duty"] },
  },
  {
    topicId: "renting",
    urgency: "high",
    problem: { ko: "집주인이 수리를 안 해주거나 본드를 돌려주지 않아요", en: "My landlord will not make repairs or return my bond" },
    situation: { ko: "거주 중 수리 문제, 렌트비 인상, 퇴거·본드 분쟁이 생긴 경우", en: "Facing repairs, a rent increase, moving out or a bond dispute" },
    firstAction: { ko: "계약서·사진·메시지와 지급 기록을 한곳에 모으세요.", en: "Collect the lease, photos, messages and payment records in one place." },
    searchQueries: { ko: ["집주인이 본드 안 돌려줘요", "호주 집주인 수리 거부", "NSW 렌트비 인상"], en: ["landlord won't return bond NSW", "landlord refusing repairs", "NSW rent increase"] },
  },
  {
    topicId: "jobs",
    urgency: "high",
    problem: { ko: "급여를 못 받았거나 갑자기 해고됐어요", en: "I have not been paid or I was suddenly dismissed" },
    situation: { ko: "임금·근무조건 문제가 생겼거나 구직이 오래 걸리는 경우", en: "Dealing with pay or workplace issues, or a long job search" },
    firstAction: { ko: "계약서, payslip, 근무시간과 고용주 메시지를 보관하세요.", en: "Keep your contract, payslips, hours worked and employer messages." },
    searchQueries: { ko: ["호주 급여 못 받았어요", "갑자기 해고됐어요", "호주 이력서 어떻게"], en: ["employer did not pay me Australia", "unfair dismissal", "Australian resume help"] },
  },
  {
    topicId: "school",
    urgency: "medium",
    problem: { ko: "학교 등록에 필요한 주소 증명이나 서류가 부족해요", en: "I do not have all the address evidence or documents needed for school enrolment" },
    situation: { ko: "이사 직후 자녀 학교를 찾거나 등록 서류를 준비하는 경우", en: "Finding a school after moving or preparing enrolment documents" },
    firstAction: { ko: "거주 주의 교육청에서 학군과 필수 서류 목록을 확인하세요.", en: "Check the catchment and required documents with your state education department." },
    searchQueries: { ko: ["학교 등록 주소 증명 없어요", "시드니 공립학교 등록 서류", "호주 학군 확인"], en: ["school enrolment without proof of address", "NSW public school documents", "school catchment check"] },
  },
  {
    topicId: "korean-passport",
    urgency: "high",
    problem: { ko: "호주에서 한국 여권이 만료됐거나 영사관 서류가 급해요", en: "My Korean passport expired in Australia or I urgently need a consular document" },
    situation: { ko: "여권 갱신, 재외국민·병역·공증 업무가 필요한 경우", en: "Needing passport renewal, overseas Korean, military or notarisation services" },
    firstAction: { ko: "관할 한국 공관과 필요한 예약·원본 서류를 확인하세요.", en: "Check the responsible Korean mission, appointment rules and original documents." },
    searchQueries: { ko: ["호주에서 한국 여권 만료", "영사관 예약 못했어요", "재외국민 등록 서류"], en: ["Korean passport expired Australia", "Korean consulate appointment", "overseas Korean registration documents"] },
  },
  {
    topicId: "local-meetups",
    urgency: "low",
    problem: { ko: "이사 온 동네에서 함께할 사람을 찾기 어려워요", en: "It is hard to meet people after moving to a new area" },
    situation: { ko: "친구, 스터디, 운동이나 같은 생활 단계의 모임을 찾는 경우", en: "Looking for friends, study, sport or people at the same life stage" },
    firstAction: { ko: "지역과 관심사를 정해 현재 열려 있는 소규모 모임을 찾아보세요.", en: "Choose your area and interest, then browse current small meetups." },
    searchQueries: { ko: ["시드니 한인 모임", "호주에서 친구 만들기", "동네 스터디"], en: ["Korean meetup Sydney", "make friends in Australia", "local study group"] },
  },
  ...EXPANDED_LIFE_TOPICS.map((item): TopicProblem => ({
    topicId: item.id,
    urgency: item.urgency,
    problem: item.problem,
    situation: item.situation,
    firstAction: item.firstAction,
    searchQueries: item.searchQueries,
  })),
];

export function getTopicProblem(topicId: string): TopicProblem | undefined {
  return TOPIC_PROBLEMS.find((item) => item.topicId === topicId);
}

export function getTopicProblemSearchTerms(topicId: string): string[] {
  const item = getTopicProblem(topicId);
  if (!item) return [];
  return [
    item.problem.ko,
    item.problem.en,
    item.situation.ko,
    item.situation.en,
    item.firstAction.ko,
    item.firstAction.en,
    ...item.searchQueries.ko,
    ...item.searchQueries.en,
  ];
}

export function getTopicProblemUrgencyLabel(
  urgency: TopicProblemUrgency,
  locale: TopicProblemLocale,
): string {
  const labels = {
    low: { ko: "천천히 알아봐도 돼요", en: "Plan ahead" },
    medium: { ko: "미리 확인하세요", en: "Check soon" },
    high: { ko: "지금 확인하세요", en: "Check now" },
  } as const;
  return labels[urgency][locale];
}
import { EXPANDED_LIFE_TOPICS } from "./topic-catalog-expansion";
